import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Info } from "lucide-react";
import HudFrame from "@/components/primitives/HudFrame";

/**
 * Q-Band Status — adaptive throughput status indicator.
 *
 * v1 behavior (NO real Q-Band backend yet):
 *   • Animated bar gently oscillates between 60–85% on a slow 4s loop
 *   • Mode toggles BALANCED / OPTIMIZED on a slow random cadence (no real logic)
 *   • Tooltip on (i) icon explains the doctrine
 *
 * This is decorative/aspirational. It communicates the ecosystem narrative
 * ("No tokens. Adaptive throughput.") without misleading users — there's no
 * real Q-Band engine wired yet.
 */
export default function QBandStatus() {
    const [throughput, setThroughput] = useState(72);
    const [mode, setMode] = useState("BALANCED");
    const [tipOpen, setTipOpen] = useState(false);

    // Oscillate throughput 60–85% on a slow seedless loop
    useEffect(() => {
        let raf;
        const start = performance.now();
        const tick = (now) => {
            const t = (now - start) / 1000;
            // Smooth sine + small random noise (deterministic-ish)
            const base = 72.5 + Math.sin(t * 0.6) * 12.5;
            const noise = Math.sin(t * 4.3) * 1.5;
            setThroughput(Math.round(base + noise));
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, []);

    // Toggle mode slowly
    useEffect(() => {
        const id = setInterval(
            () => setMode((m) => (m === "BALANCED" ? "OPTIMIZED" : "BALANCED")),
            18000,
        );
        return () => clearInterval(id);
    }, []);

    return (
        <HudFrame
            bracketColor="var(--tca-cyan-core)"
            bracketSize={12}
            borderTint="rgba(0,219,255,0.18)"
            className="bg-tca-bg-panel/65 backdrop-blur-md"
            innerClassName="p-4"
            data-testid="qband-status"
        >
            {/* Eyebrow + info */}
            <div className="flex items-center justify-between">
                <span className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/85">
                    // Q-Band · adaptive
                </span>
                <button
                    type="button"
                    aria-label="What is Q-Band?"
                    data-testid="qband-info-btn"
                    onClick={() => setTipOpen((s) => !s)}
                    onMouseEnter={() => setTipOpen(true)}
                    onMouseLeave={() => setTipOpen(false)}
                    className="relative text-tca-ink-muted hover:text-tca-cyan transition-colors"
                >
                    <Info size={12} />
                    {tipOpen && (
                        <motion.span
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="absolute right-0 top-5 z-30 w-56 p-3 bg-tca-bg-deep/95 backdrop-blur-md border border-tca-cyan/30 text-left text-[11px] leading-snug text-tca-ink-muted normal-case tracking-normal"
                            style={{ boxShadow: "0 12px 28px rgba(0,219,255,0.20)" }}
                            data-testid="qband-tooltip"
                        >
                            <span className="font-mono-tight text-[9px] uppercase tracking-[0.28em] text-tca-cyan/85 block mb-1">
                                // Q-Band
                            </span>
                            Adaptive bandwidth allocation across the ecosystem.
                            Replaces tokens.
                        </motion.span>
                    )}
                </button>
            </div>

            {/* Bar */}
            <div className="mt-3" data-testid="qband-bar">
                <div className="relative h-1.5 bg-tca-ink-dim/40 rounded-full overflow-hidden">
                    <motion.div
                        animate={{ width: `${throughput}%` }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-tca-cyan to-tca-cyan-hi"
                        style={{ boxShadow: "0 0 12px rgba(0,219,255,0.55)" }}
                    />
                    {/* Scanning shimmer */}
                    <motion.div
                        animate={{ x: ["-30%", "130%"] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                        className="absolute inset-y-0 w-1/4"
                        style={{
                            background:
                                "linear-gradient(90deg, transparent, rgba(255,255,255,0.32), transparent)",
                        }}
                    />
                </div>
                <div className="mt-2 flex items-center justify-between font-mono-tight text-[10px] uppercase tracking-[0.22em]">
                    <span className="text-tca-ink-muted">
                        {throughput}% throughput
                    </span>
                    <span className="flex items-center gap-1.5 text-tca-cyan/85">
                        <motion.span
                            animate={{ opacity: [0.55, 1, 0.55] }}
                            transition={{ duration: 2.2, repeat: Infinity }}
                            className="inline-block w-1.5 h-1.5 rounded-full bg-tca-cyan shadow-[0_0_8px_rgba(0,219,255,0.7)]"
                        />
                        continuous · no tokens
                    </span>
                </div>
            </div>

            {/* Mode */}
            <div className="mt-3 flex items-center justify-between font-mono-tight text-[10px] uppercase tracking-[0.24em]">
                <span className="text-tca-ink-muted">▸ Mode:</span>
                <motion.span
                    key={mode}
                    initial={{ opacity: 0, y: 3 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-tca-cyan-hi"
                    data-testid="qband-mode"
                >
                    {mode}
                </motion.span>
            </div>
        </HudFrame>
    );
}
